import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Utils from '../../../Utils';
import Table from '../../../Components/Table/Table';

export default function T50v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t50Tests, setT50Tests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(true);

    const [selectedRows, setSelectedRows] = useState(t50Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getTest50TestsByUserId(currentUser._id);
            if (data) {
                setT50Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setTestsLoading(false);
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            volume50: 50,
            volume100: 100,
            volume200: 200,
            volume400: 400,
            time50: '00:00,000',
            time100: '00:00,000',
            time200: '00:00,000',
            time400: '00:00,000',
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest50Test(currentUser._id, {
                time50: time50Meters,
                time100: time100Meters,
                time200: time200Meters,
                time400: time400Meters,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });

            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });
            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = durationStr.match(/^([0-5]?\d):([0-5]\d),(\d{1,3})$/);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time50Meters = currentInput.time50 ? parseDuration(currentInput.time50) : 0;
    const time100Meters = currentInput.time100 ? parseDuration(currentInput.time100) : 0;
    const time200Meters = currentInput.time200 ? parseDuration(currentInput.time200) : 0;
    const time400Meters = currentInput.time400 ? parseDuration(currentInput.time400) : 0;

    const velocity50 = currentInput.time50 ? 50 / time50Meters : 0;
    const velocity100 = currentInput.time100 ? 100 / time100Meters : 0;
    const velocity200 = currentInput.time200 ? 200 / time200Meters : 0;
    const velocity400 = currentInput.time400 ? 400 / time400Meters : 0;

    const calculateCSS = (time50Meters, time100Meters, time200Meters, time400Meters) => {
        if (time50Meters === 0 || time200Meters === 0 || time400Meters === 0) {
            return 0; // Avoid division by zero
        }

        // Calculate mean (average) time and distance
        const meanTime = (time50Meters + time100Meters + time200Meters + time400Meters) / 4;
        const meanDistance = (50 + 100 + 200 + 400) / 4;

        // Calculate differences between each time value and the mean time
        const diffTime50 = time50Meters - meanTime;
        const diffTime100 = time100Meters - meanTime;
        const diffTime200 = time200Meters - meanTime;
        const diffTime400 = time400Meters - meanTime;

        // Calculate differences between each distance value and the mean distance
        const diffDist50 = 50 - meanDistance;
        const diffDist100 = 100 - meanDistance;
        const diffDist200 = 200 - meanDistance;
        const diffDist400 = 400 - meanDistance;

        // Calculate sum of products of differences
        const sumProducts =
            diffTime50 * diffDist50 +
            diffTime100 * diffDist100 +
            diffTime200 * diffDist200 +
            diffTime400 * diffDist400;

        // Calculate sum of squared differences
        const sumSquaredTimeDiffs =
            Math.pow(diffTime50, 2) +
            Math.pow(diffTime100, 2) +
            Math.pow(diffTime200, 2) +
            Math.pow(diffTime400, 2);

        // Calculate CSS
        return sumProducts / sumSquaredTimeDiffs;
    };

    const calculateMSS = time50Meters => (time50Meters ? 50 / time50Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time50m',
            Header: () => <div className={styles.center}>Time 50m (sec)</div>,
            accessor: 'time50m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time50}</div>
        },
        {
            id: 'time100m',
            Header: () => <div className={styles.center}>Time 100m (sec)</div>,
            accessor: 'time100m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time100}</div>
        },
        {
            id: 'time200m',
            Header: () => <div className={styles.center}>Time 200m (sec)</div>,
            accessor: 'time200m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time200}</div>
        },
        {
            id: 'time400m',
            Header: () => <div className={styles.center}>Time 400m (sec)</div>,
            accessor: 'time400m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time400}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div className={styles.center}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div className={styles.center}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div className={styles.center}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div className={styles.center}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div className={styles.center}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div className={styles.center}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        time50: time50Meters,
        time100: time100Meters,
        time200: time200Meters,
        time400: time400Meters,
        velocity50: velocity50,
        velocity100: velocity100,
        velocity200: velocity200,
        velocity400: velocity400
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time50Meters > 0 && time100Meters > 0 && time200Meters > 0 && time400Meters > 0
            ? [currentTestData]
            : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time50, y: 50 },
                { x: row.time100, y: 100 },
                { x: row.time200, y: 200 },
                { x: row.time400, y: 400 }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time50 > 0 && row.time100 > 0 && row.time200 > 0 && row.time400 > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity50, y: 50 },
                    { x: row.velocity100, y: 100 },
                    { x: row.velocity200, y: 200 },
                    { x: row.velocity400, y: 400 }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPage} data-tests-page>
            <header className={styles.pageHead}>
                <div className={styles.pageHeadText}>
                    <h2 className={styles.pageTitle}>50M - 100M - 200M - 400M test</h2>
                    <p className={styles.pageSub}>50 m, 100 m, 200 m and 400 m time trials · advanced swimmers · three days</p>
                </div>
                <div className={styles.difficulty}>
                    <span className={styles.difficultyLabel} aria-hidden='true'>
                        Difficulty
                    </span>
                    <ProgressBar className={styles.difficultyBar}>
                        <ProgressBar variant='danger' now={90} aria-label='Test difficulty' />
                    </ProgressBar>
                </div>
            </header>
            <div className={styles.entryGrid}>
                <section className={`${styles.panel} ${styles.areaForm}`} aria-labelledby='t50-data-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t50-data-title' className={styles.panelTitle}>
                                Test data
                            </h3>
                            <p className={styles.panelSub}>
                                Enter your times as mm:ss,ms. Velocity is calculated as you type.
                            </p>
                        </div>
                    </div>
                    <form className={`${styles.panelBody} ${styles.entryForm}`}>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>50 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t50-volume50' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t50-volume50'
                                disabled={true}
                                {...register('volume50', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-time50' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t50-time50'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time50', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time50 ? true : undefined}
                                aria-describedby={errors.time50 ? 't50-time50-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time50 && (
                                <span id='t50-time50-error' className={styles.fieldError}>
                                    {errors.time50.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-velocity50' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t50-velocity50'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume50,
                                    currentInput.time50
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>100 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t50-volume100' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t50-volume100'
                                disabled={true}
                                {...register('volume100', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-time100' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t50-time100'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time100', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time100 ? true : undefined}
                                aria-describedby={errors.time100 ? 't50-time100-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time100 && (
                                <span id='t50-time100-error' className={styles.fieldError}>
                                    {errors.time100.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-velocity100' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t50-velocity100'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume100,
                                    currentInput.time100
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>200 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t50-volume200' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t50-volume200'
                                disabled={true}
                                {...register('volume200', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-time200' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t50-time200'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time200', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time200 ? true : undefined}
                                aria-describedby={errors.time200 ? 't50-time200-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time200 && (
                                <span id='t50-time200-error' className={styles.fieldError}>
                                    {errors.time200.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-velocity200' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t50-velocity200'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume200,
                                    currentInput.time200
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>400 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t50-volume400' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t50-volume400'
                                disabled={true}
                                {...register('volume400', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-time400' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t50-time400'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time400', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time400 ? true : undefined}
                                aria-describedby={errors.time400 ? 't50-time400-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time400 && (
                                <span id='t50-time400-error' className={styles.fieldError}>
                                    {errors.time400.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-velocity400' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t50-velocity400'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume400,
                                    currentInput.time400
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={`${styles.fieldGroup} ${styles.detailsGroup}`}>
                        <legend className={styles.fieldGroupLegend}>Session details</legend>
                        <div className={styles.field}>
                            <label htmlFor='t50-poolSize' className={styles.fieldLabel}>
                                Pool size (m) <span className={styles.fieldHint}>· for reference only</span>
                            </label>
                            <select
                                id='t50-poolSize'
                                className={`${styles.fieldInput} form-control`}
                                {...register('poolSize')}
                            >
                                <option value={''}>-</option>
                                <option value={25}>25m</option>
                                <option value={50}>50m</option>
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t50-testDate' className={styles.fieldLabel}>
                                Test date & time <span className={styles.fieldHint}>· defaults to now</span>
                            </label>
                            <input
                                id='t50-testDate'
                                type='datetime-local'
                                {...register('testDate')}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <div className={styles.formActions}>
                        <button
                            className={`${styles.submitButton} btn btn-primary`}
                            onClick={handleSubmit(onSubmit)}
                        >
                            Submit
                        </button>
                    </div>
                    </form>
                </section>
                <section className={`${styles.panel} ${styles.areaSummary}`} aria-labelledby='t50-summary-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t50-summary-title' className={styles.panelTitle}>
                                Summary
                            </h3>
                            <p className={styles.panelSub}>This entry next to the latest saved values.</p>
                        </div>
                    </div>
                    <table className={styles.summaryTable}>
                        <thead>
                            <tr>
                                <th scope='col' className={styles.metricName}>
                                    Metric
                                </th>
                                <th scope='col'>Current test</th>
                                <th scope='col'>
                                    Latest for {currentUser ? currentUser.firstName : ''}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerCss}`} />
                                    Critical Swim Speed (CSS)
                                </th>
                                <td className={styles.metricValue}>
                                    {calculateCSS(
                                        time50Meters,
                                        time100Meters,
                                        time200Meters,
                                        time400Meters
                                    ).toFixed(3)}
                                </td>
                                <td className={styles.metricValue}>{currentUser?.currentCSS?.toFixed(3) || 0}</td>
                            </tr>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerMss}`} />
                                    Maximum Swim Speed (MSS)
                                </th>
                                <td className={styles.metricValue}>{calculateMSS(time50Meters).toFixed(3) || 0}</td>
                                <td className={styles.metricValue}>{currentUser?.currentMSS?.toFixed(3) || 0}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className={styles.summaryUnit}>Velocities in m/sec.</p>
                </section>
                <section
                    className={`${styles.panel} ${styles.areaHistory}`}
                    aria-labelledby='t50-history-title'
                    data-tests-history
                >
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t50-history-title' className={styles.panelTitle}>
                                Previous tests
                            </h3>
                            <p className={styles.panelSub}>
                                Select a row to show or hide it on the charts below. Your current entry is added once its
                                times are valid.
                            </p>
                        </div>
                    </div>
                    <div className={styles.panelFlush}>
                        <Table
                            onRowClick={handleRowClick}
                            columns={columns}
                            data={t50Tests}
                            isLoading={testsLoading}
                            rowsPerPage={9}
                            updatePageSize={false}
                            pageSkipping={false}
                            selected={selectedRows}
                            showSelectionIndicator={true}
                            tableLayout='auto'
                        />
                    </div>
                </section>
                <section className={`${styles.panel} ${styles.areaGuide}`} aria-labelledby='t50-guide-title'>
                    <div className={styles.panelHead}>
                        <h3 id='t50-guide-title' className={styles.panelTitle}>
                            How to run this test
                        </h3>
                    </div>
                    <div className={`${styles.panelBody} ${styles.guide}`}>
                    <p>
                        This test is intended for <b>advanced</b> swimmers. It takes{' '}
                        <b>three</b> days to complete this test.
                    </p>
                    <p>
                        On the first day you will complete the <b>50</b> and <b>400</b> meter
                        tests with at least half an hour between them.
                    </p>
                    <p>
                        On the second day you will complete the <b>100-meter</b> test.
                    </p>
                    <p>
                        On the third day you will complete the <b>200-meter</b> test.
                    </p>
                    <p>The four trials will start in the pool without a start.</p>
                    </div>
                </section>
            </div>
            <div className={styles.chartGrid}>
                <section className={styles.panel} aria-labelledby='t50-chart-time-title'>
                    <h3 id='t50-chart-time-title' className={styles.srOnly}>
                        Distance - Time
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Time'}
                            titleY={'Distance (m)'}
                            titleX={'Time (sec)'}
                            datasets={datasetsDistanceTime}
                            xAxisType='linear'
                        />
                    </div>
                </section>
                <section className={styles.panel} aria-labelledby='t50-chart-velocity-title'>
                    <h3 id='t50-chart-velocity-title' className={styles.srOnly}>
                        Distance - Velocity
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Velocity'}
                            titleY={'Distance (m)'}
                            titleX={'Velocity (m/sec)'}
                            datasets={datasetsDistanceVelocity}
                            xAxisType='linear'
                        />
                    </div>
                </section>
            </div>
            <p className={styles.footNote}>
                For your Training Zones Limits check your{' '}
                <Link to='../../dashboard' className={styles.footLink}>
                    Dashboard
                </Link>
                .
            </p>
        </div>
    );
}
